/**
 * Подписи общих характеристик товара (пол, сезон, страна…). Кабинет продавца
 * хранит их кодами (`gender: "women"`), а витрина печатала `categoryAttributes`
 * как есть — покупатель видел строку «gender  women». Словарь один на все
 * клиенты, чтобы кабинет, витрина и консоль называли одно и то же одинаково.
 */

export type AttributeLocale = "ru" | "en"

type Dict = Record<AttributeLocale, string>

const KEY_LABELS: Record<string, Dict> = {
  gender: { ru: "Пол", en: "Gender" },
  season: { ru: "Сезон", en: "Season" },
  purpose: { ru: "Назначение", en: "Purpose" },
  audience: { ru: "Для кого", en: "For whom" },
  genre: { ru: "Жанр", en: "Genre" },
  countryOfOrigin: { ru: "Страна изготовления", en: "Country of origin" },
  composition: { ru: "Состав", en: "Composition" },
  itemType: { ru: "Тип", en: "Type" },
}

const VALUE_LABELS: Record<string, Record<string, Dict>> = {
  gender: {
    men: { ru: "Мужской", en: "Men" },
    women: { ru: "Женский", en: "Women" },
    unisex: { ru: "Унисекс", en: "Unisex" },
  },
  season: {
    spring: { ru: "Весна", en: "Spring" },
    summer: { ru: "Лето", en: "Summer" },
    autumn: { ru: "Осень", en: "Autumn" },
    winter: { ru: "Зима", en: "Winter" },
    all: { ru: "Всесезон", en: "All seasons" },
  },
  audience: {
    professionals: { ru: "Для профессионалов", en: "Professionals" },
    amateurs: { ru: "Для любителей", en: "Amateurs" },
    kids: { ru: "На каждый день", en: "Everyday" },
    men: { ru: "Для мужчин", en: "Men" },
    women: { ru: "Для женщин", en: "Women" },
    everyone: { ru: "Для всех", en: "Everyone" },
  },
  countryOfOrigin: {
    ru: { ru: "Россия", en: "Russia" },
    by: { ru: "Беларусь", en: "Belarus" },
    kz: { ru: "Казахстан", en: "Kazakhstan" },
    am: { ru: "Армения", en: "Armenia" },
    kg: { ru: "Киргизия", en: "Kyrgyzstan" },
    uz: { ru: "Узбекистан", en: "Uzbekistan" },
    ge: { ru: "Грузия", en: "Georgia" },
    tr: { ru: "Турция", en: "Turkey" },
    cn: { ru: "Китай", en: "China" },
    in: { ru: "Индия", en: "India" },
    vn: { ru: "Вьетнам", en: "Vietnam" },
    bd: { ru: "Бангладеш", en: "Bangladesh" },
    kr: { ru: "Южная Корея", en: "South Korea" },
    jp: { ru: "Япония", en: "Japan" },
    it: { ru: "Италия", en: "Italy" },
    fr: { ru: "Франция", en: "France" },
    es: { ru: "Испания", en: "Spain" },
    pt: { ru: "Португалия", en: "Portugal" },
    de: { ru: "Германия", en: "Germany" },
    pl: { ru: "Польша", en: "Poland" },
    gb: { ru: "Великобритания", en: "United Kingdom" },
    us: { ru: "США", en: "USA" },
    other: { ru: "Другая", en: "Other" },
  },
}

const localeOf = (locale: string | undefined): AttributeLocale => (locale?.startsWith("en") ? "en" : "ru")

/** Подпись ключа; неизвестный ключ (атрибут схемы категории) — как есть. */
export const attributeLabel = (key: string, locale?: string, fallback?: string): string =>
  KEY_LABELS[key]?.[localeOf(locale)] ?? fallback ?? key

/** Подпись значения; «для кого» хранится списком через запятую. */
export const attributeValueLabel = (
  key: string,
  value: string | number | boolean,
  locale?: string,
): string => {
  const loc = localeOf(locale)
  if (typeof value === "boolean") return loc === "en" ? (value ? "Yes" : "No") : value ? "Да" : "Нет"
  const text = String(value)
  const dict = VALUE_LABELS[key]
  if (!dict) return text
  return text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => dict[part]?.[loc] ?? part)
    .join(", ")
}

/** Готовые строки «подпись — значение» для карточки. Пустые значения отброшены. */
export const formatProductAttributes = (
  attributes: Record<string, string | number | boolean> | undefined,
  locale?: string,
  schemaLabels: Record<string, string> = {},
): [string, string][] =>
  Object.entries(attributes ?? {})
    .filter(([, value]) => value !== "" && value != null)
    .map(([key, value]) => [attributeLabel(key, locale, schemaLabels[key]), attributeValueLabel(key, value, locale)])

/** Узел пути категории: имя и, у корня-аудитории, пол (`female`/`male`/`kids`). */
export type SpecCategoryNode = { name: string; gender?: string | null }

/**
 * «Категория» и «Тип» для раскрытых характеристик карточки (кадр «Карточка
 * товара в наличии»: Категория «Верхняя одежда», Тип «Пуховик»; Айталы 13.10).
 *
 * Дерево: аудитория → тип → вид («Женщинам / Обувь / Кроссовки и кеды»).
 * Корень-аудитория («Женщинам») — не категория товара, его отбрасываем.
 *
 * Если у товара заполнена характеристика «Тип» (`itemType`, 19.10.2026), то
 * «Категория» — конечный узел («Верхняя одежда»), «Тип» — сама характеристика
 * («Пуховик»). Без неё — как раньше: предпоследний узел — «Категория»,
 * последний — «Тип». Один узел — только «Категория». Пустой путь — обе
 * строки пустые, витрина их не рисует.
 */
export const specCategoryAndType = (
  path: readonly SpecCategoryNode[],
  itemType?: string | number | boolean | null,
): { category: string; type: string } => {
  const audience = new Set(["female", "male", "kids"])
  const nodes = path.filter((node, index) => !(index === 0 && path.length > 1 && audience.has(node.gender ?? "")))
  const names = nodes.map((node) => node.name.trim()).filter(Boolean)
  const ownType = typeof itemType === "string" ? itemType.trim() : ""
  if (ownType) return { category: names[names.length - 1] ?? "", type: ownType }
  if (names.length === 0) return { category: "", type: "" }
  if (names.length === 1) return { category: names[0] ?? "", type: "" }
  return { category: names[names.length - 2] ?? "", type: names[names.length - 1] ?? "" }
}
